/**
 * One scroll lock for every overlay on the storefront.
 *
 * Each drawer used to lock the page itself with
 * `document.body.style.overflow = 'hidden'`, and on this site that does
 * nothing at all: the scrolling element here is <html>, not <body>
 * (`document.scrollingElement === document.documentElement`). With the filter
 * drawer open you could still scroll the shop to 600px, which is exactly what
 * it felt like on a phone: you drag the filters and the page behind slides
 * instead. So the lock targets <html> and stops touch chaining past the overlay
 * with overscroll-behavior.
 *
 * `registerLenis` is the hook for a smooth-scroll engine, which would need to be
 * stopped as well because it moves the page programmatically from its own wheel
 * handler and drives straight past `overflow: hidden`. Nothing calls it today:
 * Lenis was removed on 2026-07-30 and scrolling is native (see ./motion.js).
 *
 * Locks are keyed and counted, so a cart opened on top of the filters does not
 * unlock the page when only the cart closes, and calling lock twice for the
 * same overlay is harmless (Alpine's x-effect can re-run).
 */

const held = new Set();

let lenis = null;
let savedScroll = 0;

/** Called once by the motion layer, which owns the Lenis instance. */
export function registerLenis(instance) {
    lenis = instance;

    // Alpine mounts before the motion layer starts. If an overlay somehow got
    // opened in that gap it locked with no Lenis to stop, so stop the instance
    // now rather than letting smooth scroll run underneath a locked page.
    if (held.size > 0) {
        lenis?.stop();
    }
}

export function lock(key = 'overlay') {
    if (held.has(key)) return;
    held.add(key);
    if (held.size > 1) return; // already locked by another overlay
    apply();
}

export function unlock(key = 'overlay') {
    if (!held.delete(key)) return;
    if (held.size > 0) return; // something else still needs the page still
    release();
}

/** Escape hatch for a page teardown that must not leave the page frozen. */
export function unlockAll() {
    if (held.size === 0) return;
    held.clear();
    release();
}

function apply() {
    const root = document.documentElement;

    savedScroll = window.scrollY || root.scrollTop || 0;
    lenis?.stop();

    // Hiding the scrollbar reflows the page a few pixels narrower on desktop.
    // Pay the width back as padding so nothing visibly jumps.
    const gutter = window.innerWidth - root.clientWidth;
    if (gutter > 0) {
        root.style.paddingInlineEnd = `${gutter}px`;
    }

    root.style.overflow = 'hidden';
    root.style.overscrollBehavior = 'none';
    // Belt and braces: some engines put the viewport scroll on <body> instead.
    document.body.style.overflow = 'hidden';
}

function release() {
    const root = document.documentElement;

    root.style.removeProperty('overflow');
    root.style.removeProperty('overscroll-behavior');
    root.style.removeProperty('padding-inline-end');
    document.body.style.removeProperty('overflow');

    // Restore before Lenis reads the position again, or closing a drawer can
    // snap the shopper back to the top of the list they were halfway down.
    if (window.scrollY !== savedScroll) {
        window.scrollTo(0, savedScroll);
    }

    lenis?.start();
}

/**
 * Leaving the page with an overlay open must never come back to a frozen site.
 *
 * Several overlays contain plain links (quick view's "full details", the cart's
 * "view cart"), so the page can navigate away while a lock is held. A fresh
 * load is fine, it starts clean. The back button is not: a page restored from
 * the browser's back/forward cache keeps the inline `overflow: hidden` and a
 * stopped Lenis, and the shopper lands on a shop they cannot scroll.
 */
window.addEventListener('pageshow', (event) => {
    if (event.persisted) unlockAll();
});

// Drop the lock on the way out too, so nothing is left applied to a page that
// may be frozen and resumed rather than torn down.
window.addEventListener('pagehide', () => unlockAll());

